import { useEffect, useState } from "react";
import {
  AppShell,
  Avatar,
  Badge,
  Breadcrumbs,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { Link, Outlet, useLocation } from "react-router";
import { notifications } from "@mantine/notifications";
import { oauthLogout } from "../api.ts";
import { subscribeMe, getCachedMe, type AdminMe } from "./OAuthGuard.tsx";

const PATH_LABELS: Record<string, string> = {
  bots: "Bot 详情",
  analytics: "分析报表",
  basic: "基础设置",
  wheel: "转盘",
  languages: "语言",
  prizes: "奖品池",
  commands: "命令",
  push: "推送",
  "invite-claims": "邀请申报",
  fulfillment: "履约",
  payment: "支付",
  channels: "频道",
  texts: "文案",
  users: "用户",
  stats: "报表",
  chats: "频道/群聊",
  credentials: "平台凭据",
  ads: "广告列表",
  pool: "链接池",
  conversions: "转化记录",
  clicks: "访问记录",
  diagnosis: "链接诊断",
  "untracked-joins": "未归因入群",
  facebook: "Facebook",
  tiktok: "TikTok",
  google: "Google",
  sharechat: "ShareChat",
};

// 这些段在路由里不存在独立页面（只是 prefix），点击应跳到 "/" 而非自身。
// 例如 /bots/<id>：面包屑生成 "/bots" 段会指向不存在的路由抛 ErrorBoundary。
const NO_ROUTE_PREFIX_SEGMENTS = new Set(["bots"]);

interface BreadcrumbItem {
  label: string;
  href: string;
  isLast: boolean;
}

function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const all: Array<{ label: string; href: string }> = [{ label: "机器人", href: "/" }];
  let path = "";
  for (const seg of segments) {
    path = `${path}/${seg}`;
    // 无独立路由的 prefix 段（如 "bots"）跳过，不进面包屑。否则会生成
    // /bots 这种不存在的链接，点击会触发 ErrorBoundary "页面渲染时发生异常"。
    if (NO_ROUTE_PREFIX_SEGMENTS.has(seg)) continue;
    const label = PATH_LABELS[seg] ?? seg;
    all.push({ label, href: path });
  }
  return all.map((item, i) => ({ ...item, isLast: i === all.length - 1 }));
}

export function AdminLayout() {
  const [me, setMe] = useState<AdminMe | null>(getCachedMe());
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();
  useEffect(() => subscribeMe(setMe), []);

  const onLogout = async () => {
    try {
      const url = await oauthLogout();
      window.location.href = url;
    } catch (e) {
      notifications.show({ color: "red", message: "登出失败" });
      console.error(e);
    }
  };

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 220, breakpoint: "sm" }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Breadcrumbs>
            {breadcrumbs.map((crumb) =>
              crumb.isLast ? (
                <Text key={crumb.href} size="sm" c="dark">
                  {crumb.label}
                </Text>
              ) : (
                <Text
                  key={crumb.href}
                  size="sm"
                  c="blue"
                  component={Link}
                  to={crumb.href}
                  style={{ textDecoration: "none" }}
                >
                  {crumb.label}
                </Text>
              ),
            )}
          </Breadcrumbs>
          {me !== null ? (
            <Menu shadow="md" width={180}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs" style={{ cursor: "pointer" }}>
                    <Avatar {...(me.avatarUrl !== null ? { src: me.avatarUrl } : {})} size="sm">
                      {me.name.slice(0, 1)}
                    </Avatar>
                    <Stack gap={0}>
                      <Text size="sm" fw={500} lh={1.2}>
                        {me.name}
                      </Text>
                      {me.telegramUsername !== null ? (
                        <Text size="xs" c="dimmed" lh={1.2}>
                          @{me.telegramUsername}
                        </Text>
                      ) : null}
                    </Stack>
                    <Badge color={me.role === "owner" ? "yellow" : "blue"} size="sm">
                      {me.role}
                    </Badge>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => void onLogout()}>退出登录</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : null}
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="xs">
        <Stack gap={4} mb="md" px="xs" pt="xs">
          <Text fw={800} size="xl" c="blue" lh={1}>
            MIP
          </Text>
          <Text size="xs" c="dimmed" lh={1}>
            Turntable Admin
          </Text>
        </Stack>
        <NavLink component={Link} to="/" label="机器人" active={location.pathname === "/"} />
        <NavLink
          component={Link}
          to="/analytics"
          label="分析报表"
          active={location.pathname.startsWith("/analytics")}
        />
        <NavLink
          component={Link}
          to="/texts"
          label="文案库"
          active={location.pathname.startsWith("/texts")}
        />
        <NavLink
          component={Link}
          to="/payment/instances"
          label="支付实例"
          active={location.pathname.startsWith("/payment/instances")}
        />
        <NavLink
          component={Link}
          to="/payment/grant-outbox"
          label="派彩队列"
          active={location.pathname.startsWith("/payment/grant-outbox")}
        />
        <NavLink
          component={Link}
          to="/monitor"
          label="实时监控"
          active={location.pathname.startsWith("/monitor")}
        />
        <NavLink
          component={Link}
          to="/audit"
          label="审计日志"
          active={location.pathname.startsWith("/audit")}
        />
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
